import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowLeft, ChevronDown, Plus, Info, FileText, FileSpreadsheet } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BudgetImportDialog } from "@/components/billing/BudgetImportDialog";
import { SiteFeatureGate } from "@/components/trial-site/SiteFeatureGate";
import { TrialProtocolBillingManager } from "@/components/trial-site/TrialProtocolBillingManager";
import { TrialPatientManager } from "@/components/trial-site/TrialPatientManager";
import { InvoiceManager } from "@/components/billing/InvoiceManager";
import { BillingReconciliationPanel } from "@/components/billing/BillingReconciliationPanel";
import { PatientStipendTracker } from "@/components/finance/PatientStipendTracker";
import { useActiveTrials } from "@/hooks/useActiveTrials";
import { AddActiveTrialDialog } from "@/components/trial-site/AddActiveTrialDialog";
import { TrialAIExtractionPanel } from "@/components/trial-site/TrialAIExtractionPanel";
import { TrialOnboardingWizard } from "@/components/trial-site/TrialOnboardingWizard";
import { InstallTrialWizard } from "@/components/trial-site/install/InstallTrialWizard";
import { TrialSetupChecklist } from "@/components/trial-site/TrialSetupChecklist";
import { AssignPatientToTrialDialog } from "@/components/trial-site/AssignPatientToTrialDialog";
import { TooltipHelp } from "@/components/ui/tooltip-help";
import { ProcessStepsGuide } from "@/components/ui/process-steps-guide";
import { TrialProtocolDetailEditor } from "@/components/trial-site/TrialProtocolDetailEditor";
import { Plus as PlusIcon, Settings as SettingsIcon, Users as UsersIcon, DollarSign as DollarSignIcon } from "lucide-react";
import { useState, useEffect } from "react";
import { useRoutePrefetch } from "@/hooks/useRoutePrefetch";

export default function SiteActiveTrialsBilling() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [expandedTrialId, setExpandedTrialId] = useState<string | null>(null);
  const [newlyCreatedTrialId, setNewlyCreatedTrialId] = useState<string | null>(null);
  const [showTrialPicker, setShowTrialPicker] = useState(false);
  const [importTrialId, setImportTrialId] = useState<string | null>(null);
  const [pickedTrialId, setPickedTrialId] = useState<string>("");
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [installTrial, setInstallTrial] = useState<{ id: string; title: string } | null>(null);
  const [enrollTrialId, setEnrollTrialId] = useState<string | null>(null);
  
  // Prefetch the dashboard route for instant "Back" navigation
  const { prefetchTrialSite, prefetchRouteChunk } = useRoutePrefetch();
  
  useEffect(() => {
    if (siteId) {
      // Prefetch dashboard data immediately so "back" is instant
      prefetchTrialSite(siteId);
      prefetchRouteChunk('trial-site');
    }
  }, [siteId, prefetchTrialSite, prefetchRouteChunk]);

  const handleTrialCreated = (trialId: string, trialTitle: string) => {
    setExpandedTrialId(trialId);
    setNewlyCreatedTrialId(trialId);
  };

  const { data: site } = useQuery({
    queryKey: ["trial-site", siteId],
    queryFn: async () => {
      if (!siteId) return null;
      
      const { data, error } = await supabase
        .from("trial_sites_safe")
        .select(`
          *,
          organizations (
            id,
            name,
            type
          )
        `)
        .eq("id", siteId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!siteId
  });

  const { data: trials = [], isLoading } = useActiveTrials(site?.organizations?.id || "");

  if (!siteId) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <p className="text-center">Site ID is required</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead
        title="Active Trials & Billing - Revenue Tier"
        description="Manage active trials, protocol fee schedules, billable events, and invoicing"
        url={`/trial-site/${siteId}/billing`}
      />

      <SiteFeatureGate siteId={siteId} featureName="billing_dashboard">
        <div className="container mx-auto px-4 py-8 pb-24 md:pb-8 space-y-6">
          {/* Header */}
          <div className="mb-6">
            <Button variant="ghost" onClick={() => navigate(`/trial-site/${siteId}`)} className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Site Dashboard
            </Button>
            
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold">Active Trials & Billing</h1>
              <Badge variant="secondary">Revenue Tier</Badge>
              <TooltipHelp 
                type="info"
                content="Manage your active clinical trials, configure protocol fee schedules, track patient status, and auto-generate billable events for sponsor invoicing."
              />
            </div>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <p className="text-muted-foreground">
                Manage active trials, protocol fee schedules, billable events, and invoicing
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setPickedTrialId(""); setShowTrialPicker(true); }}
                disabled={trials.length === 0}
              >
                <FileSpreadsheet className="h-4 w-4 mr-1" />
                Import Budget
              </Button>
            </div>
          </div>

          {/* Active Trials Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  Active Trials
                  <TooltipHelp 
                    type="help"
                    content="These are trials your site has contracted with sponsors. Click on a trial to configure fees, assign patients, and view billing events."
                  />
                </CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowAddDialog(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Quick Add
                  </Button>
                  <Button size="sm" onClick={() => setShowOnboardingWizard(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Full Onboarding
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {isLoading ? (
                <div className="animate-pulse space-y-3">
                  {[1, 2, 3].map(i => <div key={i} className="h-16 bg-muted rounded" />)}
                </div>
              ) : trials.length === 0 ? (
                <div className="text-center py-8 space-y-6">
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <Info className="h-5 w-5" />
                    <span>No active trials yet. Here's how it works:</span>
                  </div>
                  
                  <ProcessStepsGuide
                    steps={[
                      {
                        title: "Add Active Trial",
                        description: "Enter trial details from your sponsor contract (title, NCT ID, phase, dates)",
                        icon: PlusIcon,
                        badge: "Step 1",
                        status: "current"
                      },
                      {
                        title: "Configure Protocol",
                        description: "Set up billable events (visits, milestones, procedures) with amounts",
                        icon: SettingsIcon,
                        badge: "Step 2",
                        status: "upcoming"
                      },
                      {
                        title: "Assign Patients",
                        description: "Add patients to the trial and track their status progression",
                        icon: UsersIcon,
                        badge: "Step 3",
                        status: "upcoming"
                      },
                      {
                        title: "Auto-Billing",
                        description: "When patient status changes, billing events are auto-generated for invoicing",
                        icon: DollarSignIcon,
                        badge: "Step 4",
                        status: "upcoming"
                      }
                    ]}
                    layout="grid"
                  />
                  
                  <Button size="lg" onClick={() => setShowAddDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Get Started - Add Your First Trial
                  </Button>
                </div>
              ) : (
                trials.map((trial) => (
                  <Collapsible 
                    key={trial.id}
                    open={expandedTrialId === trial.id}
                    onOpenChange={(open) => setExpandedTrialId(open ? trial.id : null)}
                  >
                    <div className="flex items-center gap-2 w-full p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                      <CollapsibleTrigger className="flex-1 text-left">
                        <div className="flex items-center gap-4 flex-1 text-left">
                          <div>
                            <h3 className="font-semibold">{trial.title}</h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                              <Badge variant="outline">{trial.nct_id}</Badge>
                              <span>•</span>
                              <span>{trial.phase}</span>
                              <span>•</span>
                              <Badge variant={trial.status === 'active' ? 'default' : 'secondary'}>
                                {trial.status}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setInstallTrial({ id: trial.id, title: trial.title })}
                        >
                          Install at site
                        </Button>
                        <CollapsibleTrigger asChild>
                          <Button size="sm" variant="ghost" aria-label="Expand trial details">
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                    </div>
                    
                    <CollapsibleContent>
                      <div className="mt-4 space-y-4 pl-4 border-l-2 border-muted">
                        {siteId && (
                          <TrialSetupChecklist
                            croTrialId={trial.id}
                            trialSiteId={siteId}
                            onInstall={() => setInstallTrial({ id: trial.id, title: trial.title })}
                            onEnroll={() => setEnrollTrialId(trial.id)}
                          />
                        )}
                        {/* AI Extraction from filed eTMF docs */}
                        <TrialAIExtractionPanel trialId={trial.id} />

                        {/* Protocol Details Accordion */}
                        <Accordion type="single" collapsible className="w-full">
                          <AccordionItem value="protocol-details" className="border rounded-lg">
                            <AccordionTrigger className="px-4 hover:no-underline">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-primary" />
                                <span className="font-medium">Protocol Details</span>
                                <Badge variant="outline" className="ml-2">I/E Criteria & Endpoints</Badge>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <TrialProtocolDetailEditor 
                                trialId={trial.id} 
                                readOnly={false}
                              />
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>

                        {/* Protocol & Billing Section */}
                        <TrialProtocolBillingManager 
                          trialId={trial.id}
                          trialSiteId={siteId}
                          trialTitle={trial.title}
                          autoOpenWizard={newlyCreatedTrialId === trial.id}
                          onWizardClosed={() => setNewlyCreatedTrialId(null)}
                        />
                        
                        {/* Patients Section */}
                        <TrialPatientManager 
                          trialId={trial.id}
                          trialSiteId={siteId}
                        />
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))
              )}
            </CardContent>
          </Card>

          {/* Invoices Section - No Tab, Directly on Page */}
          {site?.organizations?.id && (
            <InvoiceManager trialSiteId={siteId} />
          )}

          <PatientStipendTracker siteId={siteId} />

          {siteId && <BillingReconciliationPanel trialSiteId={siteId} />}
        </div>

          <AddActiveTrialDialog
            open={showAddDialog}
            onOpenChange={setShowAddDialog}
            siteOrgId={site?.organizations?.id || ""}
            trialSiteId={siteId}
            onTrialCreated={handleTrialCreated}
          />
          <TrialOnboardingWizard
            open={showOnboardingWizard}
            onOpenChange={setShowOnboardingWizard}
            siteOrgId={site?.organizations?.id || ""}
            trialSiteId={siteId}
            onTrialCreated={handleTrialCreated}
          />

          {installTrial && siteId && (
            <InstallTrialWizard
              open={!!installTrial}
              onOpenChange={(o) => { if (!o) setInstallTrial(null); }}
              trialId={installTrial.id}
              trialSiteId={siteId}
              trialTitle={installTrial.title}
            />
          )}

          {enrollTrialId && siteId && (
            <AssignPatientToTrialDialog
              open={!!enrollTrialId}
              onOpenChange={(o) => { if (!o) setEnrollTrialId(null); }}
              trialId={enrollTrialId}
              trialSiteId={siteId}
            />
          )}

          {/* Top-level trial picker before opening the budget importer */}
          <Dialog
            open={showTrialPicker}
            onOpenChange={(o) => { setShowTrialPicker(o); if (!o) setPickerError(null); }}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Import Sponsor Budget</DialogTitle>
                <DialogDescription>
                  Choose which active trial this budget belongs to. The fee schedule will be
                  applied to that trial at this site.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2">
                <label className="text-sm font-medium">
                  Active trial <span className="text-destructive">*</span>
                </label>
                <Select
                  value={pickedTrialId}
                  onValueChange={(v) => { setPickedTrialId(v); setPickerError(null); }}
                >
                  <SelectTrigger
                    aria-invalid={!!pickerError}
                    aria-describedby={pickerError ? "trial-picker-error" : undefined}
                    className={pickerError ? "border-destructive focus:ring-destructive" : ""}
                  >
                    <SelectValue placeholder="Select a trial…" />
                  </SelectTrigger>
                  <SelectContent>
                    {trials.map((t: any) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.title} {t.nct_id ? `(${t.nct_id})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {pickerError && (
                  <p id="trial-picker-error" role="alert" className="text-sm text-destructive">
                    {pickerError}
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => { setShowTrialPicker(false); setPickerError(null); }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const valid = trials.some((t: any) => t.id === pickedTrialId);
                    if (!pickedTrialId || !valid) {
                      setPickerError("Please select an active trial to continue.");
                      return;
                    }
                    setPickerError(null);
                    setImportTrialId(pickedTrialId);
                    setShowTrialPicker(false);
                  }}
                >
                  Continue
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {importTrialId && (
            <BudgetImportDialog
              open={!!importTrialId}
              onOpenChange={(o) => { if (!o) setImportTrialId(null); }}
              trialId={importTrialId}
              trialSiteId={siteId}
              sponsorOrganizationId={
                (trials.find((t: any) => t.id === importTrialId) as any)?.sponsor_organization_id ?? null
              }
            />
          )}
      </SiteFeatureGate>
    </Layout>
  );
}
